import React, { useEffect, useRef } from 'react';

import { Formik } from 'formik';
import {
  Button,
  ErrorMessage,
  FormikDropdownField,
  FormikTextInput,
  StandardForm,
  UiInput,
} from 'ui-kit';
import * as Yup from 'yup';

import { KeyListener } from '../../../hooks/useKeyListener/KeyListener';
import { useTourContext } from 'tours/TourProvider';

export interface FormValues {
  poolName: string;
  originationType: string;
}

const validationSchema = Yup.object().shape({
  poolName: Yup.string().required('Pool name is required'),
  originationType: Yup.string().required('Origination type is required'),
});

export interface CreatePoolFormProps {
  createPool: (formValues: FormValues) => void;
  closeModal: () => void;
}

const originationTypeOptions = [
  {
    label: 'Direct',
    value: 'direct',
  },
  {
    label: 'Indirect',
    value: 'indirect',
  },
  {
    label: 'Indirect: Fintech',
    value: 'indirect_fintech',
  },
  {
    label: 'Mixed',
    value: 'mixed',
  },
  {
    label: 'Non Applicable',
    value: 'n_a',
  },
];

const initialValues = {
  poolName: '',
  originationType: originationTypeOptions[0].value,
};

const CreatePoolForm: React.FC<CreatePoolFormProps> = ({ createPool, closeModal }) => {
  const { startTour, tourDataLoading } = useTourContext();
  const hasStartedTour = useRef(false);

  // Auto-start tour when modal opens (component mounts)
  useEffect(() => {
    if (hasStartedTour.current || tourDataLoading) {
      return;
    }

    // Small delay to ensure the modal is fully rendered
    const timer = setTimeout(() => {
      const element = document.querySelector('[data-tour="create-pool-modal"]');
      if (element) {
        hasStartedTour.current = true;
        startTour('portfolio.loan-level-data.create-pool-modal');
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [tourDataLoading, startTour]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        createPool(values);
        closeModal();
      }}
    >
      {({ submitForm }) => (
        <KeyListener
          listenerData={[
            { key: 'Enter', action: submitForm },
            { key: 'Return', action: submitForm },
          ]}
        >
          <div data-tour="create-pool-modal">
            <StandardForm closeModal={closeModal}>
              <div className="mb-2 py-2 text-[18px] font-bold leading-6 text-white">
                Create A Pool
              </div>
              <div className="py-2 text-[14px] font-normal leading-5 text-slate-200">
                POOL CREATION
              </div>
              <div
                id="subheader"
                className="mb-2 py-2 text-[14px] font-normal leading-5 text-slate-200"
              >
                Name the selected carve and save your Pool.
              </div>

              <div className="mb-4 grid w-full grid-cols-2 gap-4">
                <label htmlFor="poolName" className="flex items-center">
                  Create A Pool Name
                </label>
                <FormikTextInput
                  name="poolName"
                  placeholder="Enter Name..."
                  size={'small' as 'small'}
                  variant="light"
                  containerClass="bg-gray-950"
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      submitForm();
                    }
                  }}
                />

                <label htmlFor="originationType" className="flex items-center">
                  Select Origination Type
                </label>

                <FormikDropdownField name="originationType" options={originationTypeOptions} />
              </div>
              <div className="mt-4 flex w-full justify-end py-4">
                <Button
                  buttonType="submit"
                  type="primary_light"
                  label="Create Pool"
                  data-testid="create-pool-submit-button"
                >
                  Create Pool
                </Button>
              </div>
            </StandardForm>
          </div>
        </KeyListener>
      )}
    </Formik>
  );
};

export default CreatePoolForm;
